/**
 * Derive hard input limits from a textfield attribute's validation regex,
 * restoring the static-era behavior where Navnetryk simply refused a 17th
 * character (the old layout hardcoded maxLength={16}) and Nummertryk refused
 * anything but digits.
 *
 * Only the fully-anchored single-token repetition form is safe to derive
 * from: `^.{M,N}$` or `^[...]{M,N}$` — any longer string can never match, so
 * capping the input at N is sound; with a character class, any character
 * outside it can never match either, so it is stripped while typing/pasting
 * (2026-09-25). Every other regex shape returns null and relies on the inline
 * pattern error instead.
 */
const SHAPE = /^\^(\.|\[[^\]]*\])\{(\d+),(\d+)\}\$$/;

export function maxLengthFromRegex(regex) {
  if (!regex || typeof regex !== 'string') return null;
  const m = regex.match(SHAPE);
  if (!m) return null;
  const max = parseInt(m[3], 10);
  return Number.isFinite(max) && max > 0 ? max : null;
}

/**
 * The allowed character class (e.g. `[0-9]`, `[A-Za-z ]`) when the regex has
 * the `^[...]{M,N}$` shape and the class is not negated; null otherwise.
 */
export function charClassFromRegex(regex) {
  if (!regex || typeof regex !== 'string') return null;
  const m = regex.match(SHAPE);
  if (!m || m[1] === '.' || m[1].startsWith('[^')) return null;
  try {
    // eslint-disable-next-line no-new
    new RegExp(m[1], 'u');
    return m[1];
  } catch {
    return null;
  }
}

/** Strip every character the attribute's class disallows (no class → unchanged). */
export function sanitizeForRegex(value, regex) {
  const cls = charClassFromRegex(regex);
  if (!cls) return value;
  const inner = cls.slice(1, -1);
  return String(value ?? '').replace(new RegExp(`[^${inner}]`, 'gu'), '');
}

/** `numeric` keyboard hint for digit-only classes, null otherwise. */
export function inputModeFromRegex(regex) {
  const cls = charClassFromRegex(regex);
  return cls && /^\[[0-9-]+\]$/.test(cls) && /\d/.test(cls) ? 'numeric' : null;
}

export default maxLengthFromRegex;
